import { useState, useEffect } from "react";
import { supabase, supabaseAuth } from "../supabaseClient";
import { cn } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useTheme, THEMES } from "../contexts/ThemeContext";
import { ArrowLeft, LogOut } from "lucide-react";

const Login = ({ onLogin, onBackToLanding, onGoogleLogin }) => {
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [googleSession, setGoogleSession] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const { theme, toggleTheme } = useTheme();

  // Check for existing Google session on mount
  useEffect(() => {
    const checkGoogleSession = async () => {
      try {
        const { data: { session } } = await supabaseAuth.auth.getSession();
        if (session?.user) {
          setGoogleSession(session);
          // Ensure profile exists
          await ensureCustomerProfile(session.user);
          // Auto redirect jika sudah ada session
          if (onGoogleLogin) {
            onGoogleLogin(session.user);
          }
        }
      } finally {
        setCheckingSession(false);
      }
    };
    checkGoogleSession();

    // Listen for auth state changes (untuk handle OAuth callback)
    const { data: { subscription } } = supabaseAuth.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setGoogleSession(session);
          // Create/update profile on sign in
          await ensureCustomerProfile(session.user);
          if (onGoogleLogin) {
            onGoogleLogin(session.user);
          }
        } else if (event === 'SIGNED_OUT') {
          setGoogleSession(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [onGoogleLogin]);

  // Ensure customer profile exists in database
  const ensureCustomerProfile = async (user) => {
    try {
      // Gunakan maybeSingle() untuk avoid error ketika data tidak ada
      const { data: existing, error: selectError } = await supabaseAuth
        .from('customer_profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();

      // Ignore PGRST116 error (row not found) - ini normal
      if (selectError && selectError.code !== 'PGRST116') {
        console.error('Error checking profile:', selectError);
        return; // Return early jangan block session
      }

      if (!existing) {
        // Create new profile
        const { error: insertError } = await supabaseAuth
          .from('customer_profiles')
          .insert({
            id: user.id,
            email: user.email,
            full_name: user.user_metadata?.full_name || user.user_metadata?.name,
            avatar_url: user.user_metadata?.avatar_url
          });
        
        if (insertError) {
          console.error('Error creating profile:', insertError);
          // Jangan throw error, biar session tetap persist
        }
      }
    } catch (err) {
      console.error('Error ensuring customer profile:', err);
      // Jangan throw error, biar session tetap persist
    }
  };

  // Logout Google session
  const handleGoogleLogout = async () => {
    setGoogleLoading(true);
    try {
      await supabaseAuth.auth.signOut();
      setGoogleSession(null);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setGoogleLoading(false);
    }
  };

  // Login via credential (tabel users) -> masuk ke Kasir
  const handleCredentialLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    try {
      const { data: userData, error } = await supabase
        .from("users")
        .select("*")
        .eq("username", loginForm.username)
        .eq("password_hash", loginForm.password)
        .eq("is_active", true)
        .single();

      if (error || !userData) {
        setErrorMessage("Username atau password salah, atau akun tidak aktif");
        setLoading(false);
        return;
      }

      onLogin(userData);
      setLoginForm({ username: "", password: "" });
    } catch (error) {
      console.error("Login error:", error);
      setErrorMessage("Gagal login: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Login via Google OAuth -> masuk ke Booking flow
  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setErrorMessage("");

    try {
      const { error } = await supabaseAuth.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/',
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error("Google login error:", error);
      setErrorMessage("Gagal login dengan Google: " + error.message);
      setGoogleLoading(false);
    }
  };

  return (
    <div className={theme}>
      {/* Back to Landing Button */}
      {onBackToLanding && (
        <Button
          variant="ghost"
          onClick={onBackToLanding}
          className="fixed top-4 left-4 z-50 h-10 px-4 rounded-full bg-background/80 backdrop-blur-sm border shadow-sm flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Kembali</span>
        </Button>
      )}

      {/* Theme Switcher */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="fixed top-4 right-4 z-50 h-10 w-10 rounded-full bg-background/80 backdrop-blur-sm border shadow-sm"
      >
        <span className="text-lg">{THEMES[theme]?.icon}</span>
      </Button>
      <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-sm md:max-w-4xl">
          <div className={cn("flex flex-col gap-6")}>
            <Card className="overflow-hidden p-0">
              <CardContent className="grid p-0 md:grid-cols-2">
                <form className="p-6 md:p-8" onSubmit={handleCredentialLogin}>
                  <div className="flex flex-col gap-6">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <h1 className="text-2xl font-bold">Selamat Datang</h1>
                      <p className="text-muted-foreground text-balance">
                        Login ke akun SnapMe kamu
                      </p>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="username">Username</Label>
                      <Input
                        id="username"
                        type="text"
                        placeholder="Masukkan username..."
                        value={loginForm.username}
                        onChange={(e) =>
                          setLoginForm((prev) => ({
                            ...prev,
                            username: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>

                    <div className="grid gap-2">
                      <div className="flex items-center">
                        <Label htmlFor="password">Password</Label>
                        <button
                          type="button"
                          className="ml-auto text-sm underline-offset-2 hover:underline"
                        >
                          Lupa password?
                        </button>
                      </div>
                      <Input
                        id="password"
                        type="password"
                        placeholder="Masukkan password..."
                        value={loginForm.password}
                        onChange={(e) =>
                          setLoginForm((prev) => ({
                            ...prev,
                            password: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>

                    {errorMessage && (
                      <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                        {errorMessage}
                      </div>
                    )}

                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "Loading..." : "Login Staff"}
                    </Button>

                    {/* Divider */}
                    <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                      <span className="relative z-10 bg-card px-2 text-muted-foreground">
                        Atau untuk booking
                      </span>
                    </div>

                    {/* Google Login/Logout Button */}
                    {checkingSession ? (
                      <Button variant="outline" className="w-full" disabled>
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                      </Button>
                    ) : googleSession ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                          {googleSession.user?.user_metadata?.avatar_url && (
                            <img
                              src={googleSession.user.user_metadata.avatar_url}
                              alt="Avatar"
                              className="w-10 h-10 rounded-full"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {googleSession.user?.user_metadata?.full_name || googleSession.user?.email}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {googleSession.user?.email}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            type="button"
                            variant="default"
                            className="w-full"
                            onClick={() => onGoogleLogin && onGoogleLogin(googleSession.user)}
                          >
                            Lanjut Booking
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full flex items-center justify-center gap-2"
                            onClick={handleGoogleLogout}
                            disabled={googleLoading}
                          >
                            {googleLoading ? (
                              <div className="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                            ) : (
                              <LogOut className="w-4 h-4" />
                            )}
                            Logout
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full flex items-center justify-center gap-3"
                        onClick={handleGoogleLogin}
                        disabled={googleLoading}
                      >
                        {googleLoading ? (
                          <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                        ) : (
                          <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path
                              fill="#4285F4"
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                              fill="#34A853"
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                              fill="#FBBC05"
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            />
                            <path
                              fill="#EA4335"
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            />
                          </svg>
                        )}
                        Masuk dengan Google
                      </Button>
                    )}

                    <p className="text-center text-xs text-muted-foreground">
                      Login Staff untuk akses Kasir & Admin
                      <br />
                      Login Google untuk booking sesi foto
                    </p>
                  </div>
                </form>

                <div className="bg-muted relative hidden md:flex items-center justify-center">
                  <img
                    src="/stiker logo snapme.png"
                    alt="SnapMe"
                    className="max-h-[260px] w-auto object-contain drop-shadow-[0_0_40px_rgba(59,130,246,0.6)]"
                  />
                </div>
              </CardContent>
            </Card>

            <p className="px-6 text-center text-sm text-muted-foreground">
              Dengan melanjutkan, kamu setuju dengan{" "}
              <button
                type="button"
                className="underline underline-offset-4 hover:text-primary"
              >
                Syarat & Ketentuan
              </button>{" "}
              dan{" "}
              <button
                type="button"
                className="underline underline-offset-4 hover:text-primary"
              >
                Kebijakan Privasi
              </button>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
